import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Clock, Building2, ChevronDown, ChevronUp,
  CreditCard, Banknote, AlertCircle, RefreshCw, Plus, Eye, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import AEDIcon from '@/components/icons/AEDIcon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/services/api';

// ============ CONSTANTS ============

const MORTGAGE_STATUS_COLORS = {
  NONE: 'bg-gray-600/20 text-gray-400 border-gray-600/30',
  PRE_APPROVED: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
  CONDITIONAL: 'bg-gold-600/20 text-gold-400 border-gold-600/30',
  APPROVED: 'bg-green-600/20 text-green-400 border-green-600/30',
  REJECTED: 'bg-red-600/20 text-red-400 border-red-600/30'
};

const INSTALLMENT_STATUS_COLORS = {
  PENDING: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
  PAID: 'bg-green-600/20 text-green-400 border-green-600/30',
  OVERDUE: 'bg-red-600/20 text-red-400 border-red-600/30',
  PARTIAL: 'bg-gold-600/20 text-gold-400 border-gold-600/30'
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const getDaysOverdue = (dueDate) => {
  if (!dueDate) return 0;
  const due = new Date(dueDate);
  const now = new Date();
  const diff = Math.floor((now - due) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
};

// ============ SUB-COMPONENTS ============

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => {
  const colorClasses = {
    primary: 'bg-primary/10 border-primary/30 text-primary',
    green: 'bg-green-950/20 border-green-600/30 text-green-400',
    yellow: 'bg-gold-950/20 border-gold-600/30 text-gold-400',
    gold: 'bg-gold-950/20 border-gold-600/30 text-gold-400',
    red: 'bg-red-950/20 border-red-600/30 text-red-400',
    blue: 'bg-blue-950/20 border-blue-600/30 text-blue-400'
  };

  return (
    <div className={`p-3 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest opacity-70">{title}</p>
          <p className="text-xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs opacity-50">{subtitle}</p>}
        </div>
        {Icon && <Icon className="w-6 h-6 opacity-30" />}
      </div>
    </div>
  );
};

const OverdueInstallmentCard = ({ item, onCreateCondition, onViewDeal }) => {
  const [expanded, setExpanded] = useState(false);
  const daysOverdue = getDaysOverdue(item.installment?.due_date);
  
  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="p-4 rounded-lg border border-red-600/30 bg-red-950/10 hover:bg-red-950/20 transition-all">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-600/20">
                <AlertTriangle className="w-4 h-4 text-red-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">
                  Installment #{item.installment?.sequence_number || 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Deal: {item.deal_id?.slice(0, 8).toUpperCase()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="font-bold text-red-400">{formatCurrency(item.installment?.amount)}</p>
                <p className="text-xs text-red-400/70">{daysOverdue} days overdue</p>
              </div>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-4 pt-4 border-t border-white/10">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Due Date</p>
                <p className="font-medium">{formatDate(item.installment?.due_date)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Description</p>
                <p className="font-medium">{item.installment?.description || 'Payment Due'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 mt-4">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-gold-600/50 text-gold-400 hover:bg-gold-950/30"
                onClick={() => onCreateCondition(item)}
                data-testid={`create-condition-btn-${item.installment?.installment_id}`}
              >
                <Plus className="w-3 h-3 mr-1" />
                Create Condition
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-blue-600/50 text-blue-400 hover:bg-blue-950/30"
                onClick={() => onViewDeal(item.deal_id)}
                data-testid={`view-deal-btn-${item.deal_id}`}
              >
                <Eye className="w-3 h-3 mr-1" />
                View Deal
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const MortgageAnomalyCard = ({ anomaly, onCreateCondition, onViewDeal }) => {
  const [expanded, setExpanded] = useState(false);
  const mortgage = anomaly.mortgage || {};
  
  const anomalyType = anomaly.type;
  const anomalyIcons = {
    REJECTED: AlertCircle,
    EXPIRED: Clock,
    VALUATION_FAILED: AlertTriangle
  };
  const AnomalyIcon = anomalyIcons[anomalyType] || AlertTriangle;
  
  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="p-4 rounded-lg border border-orange-600/30 bg-orange-950/10 hover:bg-orange-950/20 transition-all">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-600/20">
                <AnomalyIcon className="w-4 h-4 text-orange-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">
                  Mortgage {anomalyType?.replace(/_/g, ' ')}
                </p>
                <p className="text-xs text-muted-foreground">
                  Deal: {anomaly.deal_id?.slice(0, 8).toUpperCase()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={MORTGAGE_STATUS_COLORS[mortgage.status] || MORTGAGE_STATUS_COLORS.NONE}>
                {mortgage.status || 'Unknown'}
              </Badge>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-4 pt-4 border-t border-white/10">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Bank</p>
                <p className="font-medium">{mortgage.bank_name || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Loan Amount</p>
                <p className="font-medium">{formatCurrency(mortgage.loan_amount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">LTV</p>
                <p className="font-medium">{mortgage.ltv_percentage || 0}%</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Approval Expiry</p>
                <p className="font-medium">{formatDate(mortgage.approval_expiry_date)}</p>
              </div>
            </div>
            
            {mortgage.valuation_status === 'FAILED' && (
              <div className="p-3 rounded bg-red-950/20 border border-red-600/30">
                <p className="text-xs text-red-400">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Valuation failed - may affect deal progression
                </p>
              </div>
            )}
            
            <div className="flex items-center gap-2 mt-4">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-gold-600/50 text-gold-400 hover:bg-gold-950/30"
                onClick={() => onCreateCondition(anomaly)}
                data-testid={`create-mortgage-condition-${anomaly.deal_id}`}
              >
                <Plus className="w-3 h-3 mr-1" />
                Create Condition
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-blue-600/50 text-blue-400 hover:bg-blue-950/30"
                onClick={() => onViewDeal(anomaly.deal_id)}
              >
                <Eye className="w-3 h-3 mr-1" />
                View Deal
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// ============ MAIN COMPONENT ============

export default function PaymentsPanel() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overdueInstallments, setOverdueInstallments] = useState([]);
  const [mortgageAnomalies, setMortgageAnomalies] = useState([]);
  const [paymentStructures, setPaymentStructures] = useState([]);
  const [stats, setStats] = useState({
    totalOverdue: 0,
    totalOverdueAmount: 0,
    mortgageRejected: 0,
    mortgageExpired: 0
  });
  
  // Dialog state
  const [conditionDialog, setConditionDialog] = useState({ open: false, item: null, type: null });
  const [conditionReason, setConditionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Fetch overdue installments
      const overdueRes = await api.get('/v2/payments/overdue-installments');
      const overdue = overdueRes.data?.overdue_installments || [];
      setOverdueInstallments(overdue);
      
      // Calculate stats
      const totalOverdueAmount = overdue.reduce((sum, item) => 
        sum + (item.installment?.amount || 0), 0
      );
      
      // Fetch all payment structures to detect mortgage anomalies
      // For MVP, we'll use a dedicated endpoint or scan deals
      const anomalies = [];
      
      // Check for rejected/expired mortgages from payment structures
      // This would ideally be a backend endpoint: GET /v2/payments/mortgage-anomalies
      // For now, we'll mock the detection logic on the client side
      
      setMortgageAnomalies(anomalies);
      
      setStats({
        totalOverdue: overdue.length,
        totalOverdueAmount,
        mortgageRejected: anomalies.filter(a => a.type === 'REJECTED').length,
        mortgageExpired: anomalies.filter(a => a.type === 'EXPIRED').length
      });
      
    } catch (error) {
      console.error('Failed to fetch payment data:', error);
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Payment data refreshed');
  };

  const handleCreateCondition = async () => {
    if (!conditionDialog.item) return;
    
    setProcessing(true);
    try {
      // Create a condition via the scan endpoint with specific deal
      const dealId = conditionDialog.item.deal_id;
      
      // Trigger a scan for this specific deal
      await api.post('/v2/conditions/scan', {
        deal_ids: [dealId]
      });
      
      toast.success('Condition created and added to classification queue');
      setConditionDialog({ open: false, item: null, type: null });
      setConditionReason('');
      
      // Refresh data
      await fetchData();
    } catch (error) {
      console.error('Failed to create condition:', error);
      toast.error(error.response?.data?.detail || 'Failed to create condition');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDeal = (dealId) => {
    // Navigate to deal timeline or open in new tab
    window.open(`/admin/deals/${dealId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <AEDIcon className="w-5 h-5 text-green-400" />
            Payment Intelligence
          </h3>
          <p className="text-sm text-muted-foreground">
            Overdue installments and mortgage anomalies
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-payments-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          title="Overdue"
          value={stats.totalOverdue}
          subtitle="Installments"
          icon={AlertTriangle}
          color="red"
        />
        <StatCard
          title="Overdue Amount"
          value={formatCurrency(stats.totalOverdueAmount)}
          subtitle="Total"
          icon={Banknote}
          color="red"
        />
        <StatCard
          title="Rejected"
          value={stats.mortgageRejected}
          subtitle="Mortgages"
          icon={AlertCircle}
          color="yellow"
        />
        <StatCard
          title="Expired"
          value={stats.mortgageExpired}
          subtitle="Approvals"
          icon={Clock}
          color="yellow"
        />
      </div>

      {/* Content Sections */}
      <div className="grid grid-cols-2 gap-6">
        {/* Overdue Installments */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Banknote className="w-4 h-4 text-red-400" />
              Overdue Installments
            </h4>
            <Badge variant="outline" className="border-red-600/50 text-red-400">
              {overdueInstallments.length}
            </Badge>
          </div>
          
          <ScrollArea className="h-[400px] pr-4">
            {overdueInstallments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AEDIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No overdue installments</p>
              </div>
            ) : (
              <div className="space-y-3">
                {overdueInstallments.map((item, idx) => (
                  <OverdueInstallmentCard
                    key={item.installment?.installment_id || idx}
                    item={item}
                    onCreateCondition={(i) => setConditionDialog({ open: true, item: i, type: 'installment' })}
                    onViewDeal={handleViewDeal}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Mortgage Anomalies */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-orange-400" />
              Mortgage Anomalies
            </h4>
            <Badge variant="outline" className="border-orange-600/50 text-orange-400">
              {mortgageAnomalies.length}
            </Badge>
          </div>
          
          <ScrollArea className="h-[400px] pr-4">
            {mortgageAnomalies.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CreditCard className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No mortgage anomalies detected</p>
                <p className="text-xs mt-1">Rejections, expirations, and valuation failures appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {mortgageAnomalies.map((anomaly, idx) => (
                  <MortgageAnomalyCard
                    key={anomaly.deal_id || idx}
                    anomaly={anomaly}
                    onCreateCondition={(a) => setConditionDialog({ open: true, item: a, type: 'mortgage' })}
                    onViewDeal={handleViewDeal}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Create Condition Dialog */}
      <Dialog open={conditionDialog.open} onOpenChange={(open) => !open && setConditionDialog({ open: false, item: null, type: null })}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-gold-400" />
              Create Condition
            </DialogTitle>
            <DialogDescription>
              This will create a SUGGESTED condition for admin review.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 rounded bg-white/5 border border-white/10">
              <p className="text-sm">
                <span className="text-muted-foreground">Deal:</span>{' '}
                <span className="font-mono text-primary">
                  {conditionDialog.item?.deal_id?.slice(0, 8).toUpperCase()}
                </span>
              </p>
              <p className="text-sm mt-1">
                <span className="text-muted-foreground">Type:</span>{' '}
                <span className="text-gold-400">
                  {conditionDialog.type === 'installment' ? 'Overdue Payment' : 'Mortgage Issue'}
                </span>
              </p>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">
                Additional Notes (Optional)
              </label>
              <Textarea
                placeholder="Enter any additional context..."
                value={conditionReason}
                onChange={(e) => setConditionReason(e.target.value)}
                className="bg-white/5 border-white/10"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConditionDialog({ open: false, item: null, type: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCondition}
              disabled={processing}
              className="bg-gold-600 hover:bg-gold-700"
              data-testid="confirm-create-condition-btn"
            >
              {processing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create & Queue
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
